import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _pick from "lodash/pick";
var _dec, _class, _descriptor, _descriptor2, _descriptor3;
import { action, computed, observable, runInAction } from 'mobx';
import { Model } from 'core/model/Model';
import api from 'core/util/api';
import generateLayout, { generatePanelLayout, updateLayout } from 'app/stores/boards/generateLayout';
import isEqualLoose from 'app/util/isEqualsLoose';
import { getWidgetTitle } from 'app/views/boards/panels/widget/utils';
import { isLayoutEqual } from '@kentik/ui-shared/boards/panels';
import { titleExactMatches } from 'app/views/boards/panels/ude/metricsTreeData';
import { getQueryFieldsSignature } from 'app/views/boards/panels/ude/queryFieldsSignature';
import { showSuccessToast } from 'core/components';
import { GRID_MARGIN, GRID_ROW_HEIGHT } from './constants';
let BoardPanelModel = (_dec = observable.ref, _class = class BoardPanelModel extends Model {
  constructor(...args) {
    super(...args);
    // assigned by UdePanel
    _initializerDefineProperty(this, "queryModel", _descriptor, this);
    // used to detect not-yet-run form edits.
    _initializerDefineProperty(this, "lastAppliedQuerySignature", _descriptor2, this);
    _initializerDefineProperty(this, "isDraftDeleting", _descriptor3, this);
    // prevent layout updates while dragging
    this.isDragging = false;
    this.lastSaved = void 0;
    this.pendingSave = null;
    this.savePromise = null;
    this.isDiscardingDraft = false;
  }
  get urlRoot() {
    return `/api/ui/boards/${this.board.id}/panel`;
  }
  get defaults() {
    return {
      title: '',
      config: {
        // useful for Widgets where a titlebar may not be necessary
        showTitle: true,
        chart: {
          type: 'line'
        }
      }
    };
  }
  get omitDuringSerialize() {
    return ['savedPanel'];
  }
  get board() {
    if (!this.collection?.parent) {
      throw new Error('Panel not associated with a board');
    }
    return this.collection.parent;
  }
  get panels() {
    return this.board.panelCollection.getSubPanels(this.id);
  }

  // layout is stored on each sub panel

  get layout() {
    return generateLayout(this.panels);
  }
  set layout(value) {
    const updatedPanels = updateLayout(this.panels, value);
    this.board.bulkSavePanels(updatedPanels);
  }
  get isLayoutDirty() {
    if (!this.lastSaved) {
      return true;
    }
    const savedLayout = this.lastSaved.layout;
    const currentLayout = this.get('layout');
    return !isLayoutEqual(savedLayout, currentLayout);
  }

  /**
   * This panel has unsaved or draft changes
   */
  get isDirty() {
    return this.get('draft') || this.isReady && this.hasUnsavedChanges;
  }

  /**
   * Panel only exists as a draft (no saved non-draft version to revert to)
   */
  get hasOnlyDraftVersion() {
    return this.get('draft') && !this.get('savedPanel');
  }
  get hasUnsavedChanges() {
    if (!this.get('cdate') && !this.get('config').isDefault) {
      return true;
    }
    const panelFields = ['title', 'parent_panel_id', 'type', 'config'];
    const beforeFields = _pick(this.lastSaved, panelFields);
    const afterFields = _pick(this.serialize(), panelFields);
    return !isEqualLoose(beforeFields, afterFields);
  }

  /**
   * Panel is ready to be saved
   */
  get isReady() {
    const type = this.get('type');
    if (type === 'ude') {
      return !!this.queryModel?.hasRun;
    }
    return true;
  }
  get title() {
    const title = this.get('title');
    const type = this.get('type');
    const config = this.get('config') || {};
    if (title) {
      return title;
    }
    if (config.title) {
      return config.title;
    }
    if (type === 'widget') {
      // For widgets with an embedded query (e.g. dataExplorerQuery), prefer the query's own title
      const queryConfig = config.query;
      if (queryConfig?.query_title) {
        return queryConfig.query_title;
      }
      const widgetTitle = getWidgetTitle(config);
      if (widgetTitle) {
        return widgetTitle;
      }
      return 'Untitled Widget';
    }
    if (type === 'ude') {
      const measurement = this.queryModel?.query?.measurement;
      if (measurement) {
        const prefix = measurement.split('/')[1];
        const label = titleExactMatches[prefix];
        if (label) {
          return `Untitled ${label}`;
        }
      }
      return 'Untitled Draft';
    }
    return 'Untitled';
  }
  get itemHeight() {
    const {
      h = 2
    } = this.get('layout')[this.board.breakpoint] || {};
    return h * GRID_ROW_HEIGHT + (h - 1) * GRID_MARGIN;
  }
  get presetFilters() {
    return this.board.get('config').presetFilters || [];
  }
  getVariableMapping(variableName) {
    const variableMappings = this.get('config').variableMappings || [];
    return variableMappings.find(mapping => mapping.variableName === variableName);
  }
  toggleVariableMapping(variableName) {
    const config = this.get('config');
    const variableMappings = config.variableMappings || [];
    const existingMappingIndex = variableMappings.findIndex(mapping => mapping.variableName === variableName);
    if (existingMappingIndex > -1) {
      variableMappings[existingMappingIndex].disabled = !variableMappings[existingMappingIndex].disabled;
    }
    this.safeSet({
      config: {
        ...config,
        variableMappings
      }
    });
  }
  setVariableMapping(variableMapping) {
    const {
      variableName
    } = variableMapping;
    const config = this.get('config');
    const variableMappings = config.variableMappings || [];
    const existingMappingIndex = variableMappings.findIndex(mapping => mapping.variableName === variableName);
    if (existingMappingIndex > -1) {
      variableMappings[existingMappingIndex] = variableMapping;
    } else {
      variableMappings.push(variableMapping);
    }
    this.safeSet({
      config: {
        ...config,
        variableMappings
      }
    });
  }
  getMeasurement() {
    const query = this.get('config').data;
    return query?.measurement;
  }
  getBodyContentsHeight(layout) {
    if (this.get('config').collapsed) {
      return 0;
    }
    const currentLayout = layout || this.layout[this.board.breakpoint];
    if (!currentLayout || currentLayout.length === 0) {
      return 190; // this is what happens when there is zero items
    }
    return Math.max(...currentLayout.map(item => (item.y + item.h) * GRID_ROW_HEIGHT + (item.y + item.h - 1) * GRID_MARGIN));
  }
  async addPanel({
    title,
    type,
    config,
    parent_panel_id,
    layout
  }) {
    const subPanel = this.board.panelCollection.forge({
      title,
      type,
      config,
      parent_panel_id,
      layout
    });
    await subPanel.save({}, {
      draft: true
    });
    return subPanel;
  }
  deserialize(_data) {
    const data = _data || this.get();
    data.layout = generatePanelLayout(data);
    data.id = `${data.id}`.startsWith('i_') ? undefined : data.id;
    this.lastSaved = data.savedPanel || data;

    // Baseline the applied-query signature from the loaded config (first load only). Guarded
    // so subsequent draft-save deserializations don't overwrite the snapshot taken at apply.
    if (this.lastAppliedQuerySignature === undefined) {
      this.lastAppliedQuerySignature = getQueryFieldsSignature(data.config?.data);
    }
    return super.deserialize(data);
  }
  serialize(data) {
    data = data || this.get();
    data.layout = generatePanelLayout(data);
    return super.serialize(data);
  }
  applyQueryConfig(config) {
    this.safeSet({
      config
    });
    this.lastAppliedQuerySignature = getQueryFieldsSignature(config?.data);
  }
  async save(attributes, options = {}) {
    const {
      draft = false
    } = options;

    // makes the save button show the loading state appropriately
    this.safeSet({
      draft
    });
    if (this.savePromise) {
      this.safeSet(attributes);

      // don't overwrite a pending non-draft
      if (!this.pendingSave || this.pendingSave.options.draft) {
        this.pendingSave = {
          options
        };
      }
      await this.savePromise;
      if (this.pendingSave) {
        const {
          options: nextOptions
        } = this.pendingSave;
        this.pendingSave = null;
        return this.save({}, nextOptions);
      }
      return this.get();
    }
    if (this.board.isNew) {
      this.set(attributes);

      // creating the board with the new panel
      // will create a draft board if this is a draft panel
      return this.trackSave(this.board.save({}, options));
    }
    if (draft) {
      // save with the draft flag
      options.url = `${options.url || this.url}/?draft=true`;
      options.toast = false;
    }
    return this.trackSave(super.save(attributes, options));
  }
  async trackSave(savePromise) {
    this.savePromise = savePromise;
    try {
      await this.savePromise;
    } finally {
      this.savePromise = null;
    }
    return this.get();
  }
  async whenSaveSettled() {
    let pending = this.savePromise;
    while (pending) {
      // eslint-disable-next-line no-await-in-loop -- intentionally drain the serialized save chain
      await pending.catch(() => undefined);
      pending = this.savePromise;
    }
  }
  async discardDraft({
    toast = true
  } = {}) {
    if (this.isNew) {
      // nothing to do
      return;
    }
    const response = await api.put(`${this.url}/discardDraft`);
    if ('success' in response) {
      // panel removed (no saved state)
      this.removeFromBoard();
    } else {
      this.isDiscardingDraft = true;
      try {
        this.safeSet(this.deserialize(response));
        this.lastAppliedQuerySignature = getQueryFieldsSignature(this.get('config')?.data);
      } finally {
        this.isDiscardingDraft = false;
      }
    }
    if (toast) {
      await showSuccessToast(`Reverted panel "${this.title}"`);
    }
  }
  async draftDelete() {
    if (this.isNew) {
      this.removeFromBoard();
      return;
    }
    if (this.isDraftDeleting) {
      return;
    }
    this.isDraftDeleting = true;
    try {
      await api.del(`${this.url}?draft=true`);
      runInAction(() => {
        this.removeFromBoard();
      });
    } finally {
      runInAction(() => {
        this.isDraftDeleting = false;
      });
    }
  }
  removeFromBoard() {
    // the query model outlives the panel view; stop its live-update worker
    this.queryModel?.cleanup();
    this.collection?.remove(this.id);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "queryModel", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "lastAppliedQuerySignature", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "isDraftDeleting", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "panels", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "panels"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "layout", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "layout"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isLayoutDirty", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isLayoutDirty"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isDirty", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isDirty"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasOnlyDraftVersion", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasOnlyDraftVersion"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasUnsavedChanges", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasUnsavedChanges"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isReady", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "isReady"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "itemHeight", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "itemHeight"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "presetFilters", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "presetFilters"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "applyQueryConfig", [action], Object.getOwnPropertyDescriptor(_class.prototype, "applyQueryConfig"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "save", [action], Object.getOwnPropertyDescriptor(_class.prototype, "save"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "draftDelete", [action], Object.getOwnPropertyDescriptor(_class.prototype, "draftDelete"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeFromBoard", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeFromBoard"), _class.prototype), _class);
export default BoardPanelModel;